import React, { useState } from 'react';
import { ITableRow } from './types';
import classnames from 'classnames';
import { TableSubRow } from './TableSubRow';
import { TableExpandSubrowButton } from './TableExpandSubrowButton';
import { SvgIcon } from 'ui/SvgIcon';
import ChevronDownIcon from 'ui/Icons/tabler/chevron-down.component.svg';
import ChevronUpIcon from 'ui/Icons/tabler/chevron-up.component.svg';

interface ITableRowProps {
  row: ITableRow;
  rowIndex: number;
  evenColor: string;
  hasExpandColumn?: boolean;
}

export const TableRow: React.FC<ITableRowProps> = React.memo(({ row, rowIndex, evenColor, hasExpandColumn = false }) => {
  const visibleCells = row.cells.filter(cell => !cell.isHidden);
  const subRows = row.subRows || [];
  const isEven = rowIndex % 2 === 0;
  const hasExpandableContent = !!row.expandableContent;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  const rowClassname = classnames(
    `id-${row.id} h-15 border border-solid border-gray-20 text-15px border-l-0 border-r-0`,
    row.className,
    {
      'border-b-0': isExpanded || isContentExpanded,
      [`bg-${evenColor}`]: isEven,
    }
  );

  return (
    <>
      <tr
        key={row.id}
        className={rowClassname}
      >
        {visibleCells.map((cell, cellIndex) => {
          const cellValue = cell.renderValue ? cell.renderValue(isExpanded) : cell.value;
          return (
            <td key={cell.id} className={classnames('px-2', cell.className)} title={cell.title}>
              {subRows.length > 0 && cellIndex === 0 ? (
                <TableExpandSubrowButton isExpanded={isExpanded} setIsExpanded={setIsExpanded}>
                  <span className={cell.contentClassName}>{cellValue}</span>
                </TableExpandSubrowButton>
              ) : (
                <span className={cell.contentClassName}>{cellValue}</span>
              )}
            </td>
          );
        })}
        {hasExpandColumn && (
          <td className="w-12 px-2">
            {hasExpandableContent ? (
              <button
                type="button"
                onClick={() => setIsContentExpanded(!isContentExpanded)}
                className="flex items-center justify-center w-full cursor-pointer bg-transparent border-none p-0"
                aria-label={isContentExpanded ? 'Collapse content' : 'Expand content'}
              >
                <SvgIcon
                  IconComponent={isContentExpanded ? ChevronUpIcon : ChevronDownIcon}
                  className="w-4 fill-transparent stroke-current stroke-2"
                />
              </button>
            ) : null}
          </td>
        )}
      </tr>
      {isExpanded && subRows.map((subRow, subrowIndex) => {
        const uniqueId = `id-subrow-${row.id}-${subrowIndex}`;
          return (
            <TableSubRow
              key={uniqueId}
              subRow={subRow}
              isEven={isEven}
              evenColor={evenColor}
              uniqueId={uniqueId}
            />
          );
        })}
      {isContentExpanded && hasExpandableContent && (
        <tr
          className={classnames(
            'border border-solid border-gray-20 border-l-0 border-r-0 border-t-0',
            { [`bg-${evenColor}`]: isEven }
          )}
        >
          <td colSpan={visibleCells.length + (hasExpandColumn ? 1 : 0)} className="px-4 py-3">
            {row.expandableContent}
          </td>
        </tr>
      )}
    </>
  );
});
